export const DEFAULT_SCHOOL_REGION = {
  city: "서울특별시",
  district: "강동구"
};

export const DEFAULT_SCHOOL_REGION_ID = "서울특별시:강동구";

// Add new regions with the same city:district key and school-level groups.
export const schoolsByRegion = {
  [DEFAULT_SCHOOL_REGION_ID]: {
    "초등": [
      "강덕초", "강동초", "강명초", "강빛초", "강솔초", "강일초", "고덕초", "고명초", "고일초", "고현초",
      "길동초", "대명초", "둔촌초", "명덕초", "명원초", "명일초", "묵동초", "상일초", "선린초", "성내초",
      "성일초", "성현초", "신명초", "신암초", "암사초", "천동초", "천일초", "천호초", "한산초"
    ],
    "중등": [
      "강동중", "강명중", "강빛중", "강일중", "고덕중", "동북중", "동신중", "둔촌중", "명일중", "배재중",
      "상일중", "성내중", "성덕여중", "성현중", "신암중", "천일중", "천호중", "한산중", "한영중"
    ],
    "고등": [
      "강동고", "강일고", "광문고", "둔촌고", "둔촌고", "명일여고", "배재고", "상일미디어고", "상일고", "서울컨벤션고",
      "신암고", "성덕고", "한영고", "한영외고"
    ]
  }
};

export function getRegionId({ city, district }) {
  return `${city}:${district}`;
}

export function getRegionById(regionId) {
  const [city = "", district = ""] = String(regionId || "").split(":");
  return { city, district };
}

export function getRegionLabel(regionId) {
  const { city, district } = getRegionById(regionId);
  return city && district ? `${city} > ${district}` : "";
}

export function getSchoolRegions() {
  return Object.keys(schoolsByRegion).map((regionId) => ({
    regionId,
    ...getRegionById(regionId)
  }));
}

export function getSchoolCities() {
  return [...new Set(getSchoolRegions().map((region) => region.city))];
}

export function getSchoolDistricts(city) {
  return getSchoolRegions()
    .filter((region) => region.city === city)
    .map((region) => region.district);
}

export function getRegionalSchoolNames(region = DEFAULT_SCHOOL_REGION, schoolLevel = "") {
  const schools = schoolsByRegion[getRegionId(region)] || {};

  if (schoolLevel && schools[schoolLevel]) return [...schools[schoolLevel]];

  return Object.values(schools).flat();
}

export function getRegionalSchoolOptions(region = DEFAULT_SCHOOL_REGION, schoolLevel = "") {
  return [...new Set(getRegionalSchoolNames(region, schoolLevel))]
    .map((school) => ({ value: school, label: school }));
}

export function getAllSchoolEntries() {
  return Object.entries(schoolsByRegion).flatMap(([regionId, schools]) => (
    Object.entries(schools).flatMap(([schoolLevel, schoolNames]) => (
      schoolNames.map((name) => ({ name, schoolLevel, regionId }))
    ))
  ));
}

export function getAcademySchoolOptions(academyInfo = {}, schoolLevel = "") {
  if (Array.isArray(academyInfo.schools)) {
    return [...new Map(academyInfo.schools
      .filter((school) => school.active && (!schoolLevel || school.schoolLevel === schoolLevel))
      .map((school) => {
        // Region-qualified values preserve text-based student/exam contracts.
        const value = `${school.name} (${school.city} ${school.district})`;
        return [value, { value, label: school.name, schoolLevel: school.schoolLevel }];
      })).values()].sort((a, b) => a.value.localeCompare(b.value, "ko"));
  }
  const operatingRegionIds = Array.isArray(academyInfo.operatingRegionIds)
    ? academyInfo.operatingRegionIds
    : [DEFAULT_SCHOOL_REGION_ID];
  const additionalSchoolNames = academyInfo.additionalSchoolNames || [];
  const entries = getAllSchoolEntries();
  const regionalSchools = entries.filter((school) => operatingRegionIds.includes(school.regionId));
  const additionalSchools = entries.filter((school) => additionalSchoolNames.includes(school.name));

  return [...new Map(
    [...regionalSchools, ...additionalSchools]
      .filter((school) => !schoolLevel || school.schoolLevel === schoolLevel)
      .map((school) => [school.name, { value: school.name, label: school.name, schoolLevel: school.schoolLevel }])
  ).values()].sort((first, second) => first.label.localeCompare(second.label, "ko"));
}

export function getAdditionalSchoolOptions(academyInfo = {}) {
  const includedSchoolNames = new Set(getAcademySchoolOptions({
    ...academyInfo,
    additionalSchoolNames: []
  }).map((school) => school.value));
  const alreadyAdded = new Set(academyInfo.additionalSchoolNames || []);

  return [...new Map(getAllSchoolEntries()
    .filter((school) => !includedSchoolNames.has(school.name) && !alreadyAdded.has(school.name))
    .map((school) => [school.name, { value: school.name, label: school.name, schoolLevel: school.schoolLevel, regionId: school.regionId }])
  ).values()].sort((first, second) => first.label.localeCompare(second.label, "ko"));
}

export function matchesSchoolSearch(search, option) {
  const keyword = search.trim();
  if (!keyword) return true;

  return String(option?.label || option?.value || "").includes(keyword);
}
